import { ImmunizationRecord, ImmunizationSearch, ImmunizationSeries } from "../models/Interfaces";
import React from "react";
import { infoLog } from "../utils/logger";
import { useTranslation } from "react-i18next";
import "./patientCard.css";
import DisclaimerCard from "./Disclaimers/DisclaimerCard";
import RecordCard from "./RecordCard";
import { useNavigate } from "react-router";
import { useModal } from "../context/ModalContext";
import { IconButton } from "./IconButton/IconButton";
import ScreeningResultCard from "./ScreeningResultCard";
import { useAtom } from "jotai";
import {
  CompleteRecord,
  izProviderConfigsAtom,
  refreshingRecordsAtom,
  isHydrated,
} from "../jotai/atoms";
import TestResultsCard from "./TestResultsCard";
import getIzProvider from "../models/providers/IzProviders";
import Avatar from "./Avatar";
import { Squirrel } from "./spinner/Squirrel";
import { ImageIcon } from "./ImageIcon";

interface PatientCardProps {
  index: number;
  completeRecord: CompleteRecord;
  open: boolean;
  onOpenToggle: () => void;
  screening?: boolean;
}

export default function PatientCard({
  completeRecord,
  index,
  open,
  onOpenToggle,
  screening,
}: PatientCardProps) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const modal = useModal();
  const [providerConfigs] = useAtom(izProviderConfigsAtom);
  const [refreshingRecords] = useAtom(refreshingRecordsAtom);

  const providerConfig = isHydrated(providerConfigs)
    ? providerConfigs.find(
        (config) =>
          config.iz_provider.destination_id.toLowerCase() ===
          completeRecord.record?.izProviderKey.toLowerCase()
      )
    : undefined;
  const isRefreshing = refreshingRecords.some(
    (r) => r.attributes.uid === completeRecord.record?.uid
  );

  let izProvider;
  if (completeRecord.record?.izProviderKey) {
    izProvider = getIzProvider(completeRecord.record.izProviderKey, t);
  }

  const selectItemHandler = async (record: ImmunizationRecord) => {
    infoLog(`searchObj ${JSON.stringify(record)}`);
  };

  const goToReports = () => {
    if (completeRecord.record) {
      modal.openReportListModal(completeRecord.record);
    }
  };

  const goToAvatars = () => {
    if (completeRecord.record?.uid) {
      modal.openAvatarListModal(completeRecord.record.uid);
    }
  };

  const goToSHLink = () => {
    if (completeRecord.record?.uid) {
      modal.openSmartHealthLinkModal(completeRecord.record.uid);
    }
  };

  const ExpandedIcon = () => (
    <div>
      <i className="fa-solid fa-angle-up"></i>
    </div>
  );

  const CollapsedIcon = () => (
    <div>
      <i className="fa-solid fa-angle-down"></i>
    </div>
  );

  const emptyScreening =
    !completeRecord.screening || completeRecord.screening?.results.length === 0;

  const navigateToTestResults = (search: ImmunizationSearch) => {
    navigate("/home/records/screening", {
      state: {
        back: true,
        searchUid: search.uid,
      },
    });
  };

  return (
    <div className="md:collapse flex flex-col text-primary-content bg-secondary border-secondary md:border">
      <div
        data-testid={`patient-card-${index}`}
        data-open={open}
        className="collapse-title pr-4 flex gap-3 items-center cursor-pointer"
        onClick={onOpenToggle}
      >
        <div
          className="w-14 h-14 shrink-0"
          onClick={(e) => {
            e.stopPropagation();
            goToAvatars();
          }}
        >
          <Avatar recordId={completeRecord.record?.uid ?? ""} loading={isRefreshing} />
        </div>
        <div
          role="paragraph"
          className="text-xl font-semibold truncate"
          data-testid="search-card-name"
        >
          {completeRecord.search.isChildSearch
            ? completeRecord.search.childFirstName
            : completeRecord.search.firstName}{" "}
          {completeRecord.search.isChildSearch
            ? completeRecord.search.childLastName
            : completeRecord.search.lastName}
        </div>

        <div className="ml-auto flex gap-2">
          {!screening && (
            <>
              <button
                className="btn btn-secondary btn-circle p-1 h-fit"
                onClick={(e) => {
                  e.stopPropagation();
                  goToReports();
                }}
              >
                <div className="flex flex-col items-center">
                  <ImageIcon className="w-6" src="/assets/images/Icon_PDF_White.png" />
                  PDF
                </div>
              </button>
              {providerConfig?.features?.smart_health_links && (
                <>
                  <div className="my-auto flex">
                    <button
                      className="btn btn-secondary btn-circle p-1 h-fit"
                      onClick={(e) => {
                        e.stopPropagation();
                        goToSHLink();
                      }}
                    >
                      <div className="flex flex-col items-center">
                        <ImageIcon className="w-6" src="/assets/images/Icon_Share_White.png" />
                        URL
                      </div>
                    </button>
                  </div>
                </>
              )}
            </>
          )}
          <div className="my-auto max-md:hidden">{open ? <ExpandedIcon /> : <CollapsedIcon />}</div>
        </div>
      </div>
      <div
        className="grid transition-[grid-template-rows] duration-200 bg-secondary md:bg-base-100 dark:md:bg-secondary"
        style={{
          gridTemplateRows: open ? "1fr" : "0fr",
        }}
      >
        <div className={`overflow-hidden min-h-0 ${open ? "mb-4" : ""}`}>
          <div className="flex flex-col items-center justify-center bg-secondary">
            {!screening && <DisclaimerCard provider={completeRecord.record!.izProviderKey} />}
            {screening && (
              <DisclaimerCard provider={completeRecord.search.izProviderKey} screening />
            )}
            {izProvider?.jurisdictionAbbrev.toLowerCase() === "nj" && !screening && (
              <TestResultsCard onClick={() => navigateToTestResults(completeRecord.search)} />
            )}
          </div>
          <div className="grid grid-cols-1 mt-2 lg:grid-cols-2 2xl:grid-cols-3 gap-2 px-4 md:px-2">
            {screening ? (
              <>
                {emptyScreening ? (
                  <ScreeningResultCard key={1} screening={completeRecord.screening ?? undefined} />
                ) : (
                  <div className="flex flex-col" key={index}>
                    {completeRecord.screening?.results.map((screeningResult, index) => (
                      <ScreeningResultCard
                        key={index}
                        screening={completeRecord.screening!}
                        screeningResult={screeningResult}
                      />
                    ))}
                  </div>
                )}
              </>
            ) : (
              <>
                {completeRecord.record?.immunizations.map(
                  (imm: ImmunizationSeries, index: number) => (
                    <div className="flex flex-col h-full" key={index}>
                      <RecordCard
                        index={index}
                        record={completeRecord.record!}
                        series={imm}
                        onSelect={() => {
                          selectItemHandler(completeRecord.record!);
                        }}
                      />
                    </div>
                  )
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
